import { isAxiosError } from 'axios';
import type {
  CatalogImportCommit,
  CatalogImportPreview,
  CatalogResolution,
} from '@pokerroom/types';

export interface EnrollmentOutcome {
  memberId: string;
  state: 'enrolled' | 'skipped-existing' | 'invalid';
  reason?: string;
}

export function normalizeSelection(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}

export function buildCatalogCommitPayload(
  preview: Pick<CatalogImportPreview, 'package'>,
  resolutions: CatalogResolution[],
): CatalogImportCommit {
  return { package: preview.package, resolutions };
}

export function validateCatalogResolutions(
  preview: Pick<CatalogImportPreview, 'package' | 'conflicts' | 'errors'>,
  resolutions: CatalogResolution[],
): string | null {
  if (preview.errors.length) return 'Resolve package validation errors before commit.';

  for (const conflict of preview.conflicts) {
    const resolution = resolutions.find(
      (item) => item.kind === conflict.kind && item.sourceKey === conflict.sourceKey,
    );
    if (!resolution?.action) return `Choose a resolution for ${conflict.sourceKey}.`;
    if (!conflict.choices.includes(resolution.action))
      return `Invalid resolution for ${conflict.sourceKey}.`;
    if (
      resolution.action === 'replace' &&
      (!resolution.reason?.trim() || resolution.expectedVersion !== conflict.expectedVersion)
    )
      return `A replacement reason and current version are required for ${conflict.sourceKey}.`;
    if (resolution.action === 'rename' && !resolution.name?.trim()) {
      return `A rename value is required for ${conflict.sourceKey}.`;
    }
  }

  const omitted = new Set(
    resolutions.filter((item) => item.action === 'omit').map((item) => item.sourceKey),
  );
  for (const template of preview.package.templates) {
    if (omitted.has(template.sourceKey)) continue;
    const override = resolutions.find(
      (item) => item.kind === 'template' && item.sourceKey === template.sourceKey,
    )?.blindStructureSourceKey;
    const dependency = override === undefined ? template.blindStructureSourceKey : override;
    if (preview.package.version === 2) {
      const levelOverride = resolutions.find(
        (item) => item.kind === 'template' && item.sourceKey === template.sourceKey,
      )?.timedSettlementLevelSourceKey;
      const original = preview.package.templates.find(
        (item) => item.sourceKey === template.sourceKey,
      )!.timedSettlementLevelSourceKey;
      const levelDependency = levelOverride === undefined ? original : levelOverride;
      const level = preview.package.timedSettlementLevels.find(
        (item) => item.sourceKey === levelDependency,
      );
      if (
        (original && !levelDependency) ||
        (levelDependency &&
          (!level ||
            omitted.has(levelDependency) ||
            template.values.type !== 'TIMED' ||
            !template.values.manualCountMode ||
            template.values.startingChips !== level.values.startingChips))
      )
        return `${template.sourceKey} requires a compatible level dependency or omission.`;
    }
    if (dependency && omitted.has(dependency)) {
      return `${template.sourceKey} requires a replacement dependency or omission.`;
    }
  }
  return null;
}

export function groupEnrollmentOutcomes(outcomes: EnrollmentOutcome[]) {
  return {
    enrolled: outcomes.filter((item) => item.state === 'enrolled'),
    skipped: outcomes.filter((item) => item.state === 'skipped-existing'),
    invalid: outcomes.filter((item) => item.state === 'invalid'),
  };
}

export function catalogErrorMessage(error: unknown): string {
  if (isAxiosError(error) && error.response) {
    const data: unknown = error.response.data;
    const messages: string[] = [];
    if (data && typeof data === 'object') {
      const body = data as { message?: unknown; errors?: unknown };
      if (typeof body.message === 'string') messages.push(body.message);
      if (Array.isArray(body.message)) {
        messages.push(
          ...body.message.filter((value): value is string => typeof value === 'string'),
        );
      }
      if (body.errors && typeof body.errors === 'object') {
        for (const values of Object.values(body.errors)) {
          if (Array.isArray(values)) {
            messages.push(...values.filter((value): value is string => typeof value === 'string'));
          }
        }
      }
    }
    if (messages.length) return `HTTP ${error.response.status}: ${messages.join('; ')}`;
  }
  return error instanceof Error ? error.message : 'Catalog request failed';
}
